import { twMerge } from "tailwind-merge";

import FormBuilderDataTableCell from "containers/admin/clients/client/components/form-builder/form-builder-data-table-cell";
import {
  getFieldUiType,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";

export type FormBuilderDataTableColumn = {
  id: string;
  label: string;
  field: JsonFormSchema;
};

export type FormBuilderDataTableRow = {
  id: string;
  cells: Record<string, string>;
  rawCells: Record<string, unknown>;
};

type FormBuilderDataTableProps = {
  columns: FormBuilderDataTableColumn[];
  rows: FormBuilderDataTableRow[];
  className?: string;
};

const isMultilineColumn = (field: JsonFormSchema, value: string): boolean =>
  getFieldUiType(field) === "textarea" || value.includes("\n");

const FormBuilderDataTable = ({ columns, rows, className }: FormBuilderDataTableProps) => {
  if (columns.length === 0 || rows.length === 0) {
    return null;
  }

  const isWideColumn = (column: FormBuilderDataTableColumn): boolean => {
    if (getFieldUiType(column.field) === "textarea") {
      return true;
    }

    return rows.some((row) => (row.cells[column.id] ?? "").includes("\n"));
  };

  return (
    <div
      className={twMerge(
        "w-full overflow-x-auto rounded-md border border-gray-200 shadow-xs",
        className,
      )}
    >
      <table className="w-full min-w-max border-collapse">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100">
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={twMerge(
                  "h-10 px-4 text-left text-base font-bold leading-6 tracking-[0.25px] text-gray-900",
                  isWideColumn(column) && "min-w-80",
                )}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-b border-gray-200 last:border-b-0">
              {columns.map((column) => {
                const value = row.cells[column.id] ?? "";
                const multiline = isMultilineColumn(column.field, value);

                return (
                  <td
                    key={column.id}
                    className={twMerge(
                      "bg-white px-4 align-middle text-gray-600",
                      multiline ? "py-4" : "h-14",
                      isWideColumn(column) && "min-w-80",
                    )}
                  >
                    <FormBuilderDataTableCell
                      columnId={column.id}
                      field={column.field}
                      value={value}
                      rawValue={row.rawCells[column.id]}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FormBuilderDataTable;
